import { MapUtils } from '../../utility/map-utils.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { EventTicket } from './event-ticket.js';
import {
  DataModel,
  DataModelBuilder,
  isSameData,
  isSameList,
  SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import { Discount } from './discounts/discount.js';
import { VersionRange } from '../core/version-range.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { Refund } from './discounts/refund.js';

export class OnlineEventTicketOrderItem extends DataModel {
  static readonly TYPE_ID: string = '130b21920b544d6c98d5863995072d0d';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly tickets: EventTicket[],
    public readonly refunds: Refund[],
    public discount: Discount | null,
  ) {
    super();
  }

  get $typeId(): string {
    return OnlineEventTicketOrderItem.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OnlineEventTicketOrderItem.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    tickets: EventTicket[],
    refunds: Refund[],
    discount: Discount | null,
  }) {
    return new OnlineEventTicketOrderItem(
        args.uniqueId,
        args.businessRef,
        args.tickets,
        args.refunds,
        args.discount,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlineEventTicketOrderItem {
    return new OnlineEventTicketOrderItem(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getList<EventTicket>(map, 'tickets', (value) => {
          return context.fromData<EventTicket>(value as SerializedData);
        }),
        MapUtils.getList<Refund>(map, 'refunds', (value) => {
          return context.fromData<Refund>(value as SerializedData);
        }),
        context.fromDataOrNull<Discount>(MapUtils.getOrNull<SerializedData>(map, 'discount')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    context.storage.set('avoidOptimizeForQuery', true);

    const map = {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'tickets': this.tickets.map((e) => context.toData(e)),
      'discount': context.toDataOrNull(this.discount),
      'refunds': this.refunds.map((e) => context.toData(e)),
    };

    context.storage.remove('avoidOptimizeForQuery');
    return map;
  }

  clone(): this {
    return new OnlineEventTicketOrderItem(
        this.uniqueId,
        this.businessRef.clone(),
        this.tickets.map((e) => e.clone()),
        this.refunds.map((e) => e.clone()),
        this.discount?.clone() ?? null,
    ) as this;
  }

  isSame(other: OnlineEventTicketOrderItem): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.businessRef === this.businessRef &&
      isSameData(other.discount, this.discount) &&
      isSameList(other.tickets, this.tickets, isSameData) &&
      isSameList(other.refunds, this.refunds, isSameData);
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return new OnlineEventTicketOrderItem(
        this.uniqueId,
        this.businessRef.clone(),
        this.tickets.map((e) => e.assignToCustomerAccount(account)),
        this.refunds.map((e) => e.clone()),
        this.discount?.clone() ?? null,
    ) as this;
  }
}

export class OnlineEventTicketOrderItemBuilder extends DataModelBuilder<OnlineEventTicketOrderItem> {
  build(context: SerializationContext, map: SerializedData): OnlineEventTicketOrderItem {
    return OnlineEventTicketOrderItem.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OnlineEventTicketOrderItem.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(OnlineEventTicketOrderItem.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `discount` of type `Discount | null`.
    if(version < 2) version = 2;

    // In version 3 we added `refunds` of type `Refund[]`.
    if(version < 3) {
      version = 3;
      map['refunds'] = [];
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
